import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiAlertTriangle,
  FiX,
  FiCheck,
  FiEye,
  FiHome,
} from 'react-icons/fi';
import '../styles/LessonStyles.css';
import GetStartedWith from '../../../../components/GetStartedWith';

const trapAnswers = [
  {
    id: 1,
    type: 'Too Extreme',
    description:
      'Answer choices that use absolute language when the passage is more moderate.',
    indicators: [
      'always',
      'never',
      'all',
      'none',
      'completely',
      'entirely',
      'impossible',
      'certain',
    ],
    example: {
      passage: 'Many scientists believe that climate change is accelerating.',
      wrongAnswer:
        'All scientists are certain that climate change is accelerating.',
      whyWrong:
        'Uses "all" and "certain" when passage says "many" and "believe"',
    },
    howToAvoid:
      'Look for qualifying words in the passage like "some," "many," "often," "generally"',
  },
  {
    id: 2,
    type: 'Too Narrow',
    description:
      'Answer choices that focus on a minor detail rather than the main point.',
    indicators: [
      'focuses on one example',
      'mentions specific detail',
      'ignores broader context',
    ],
    example: {
      passage:
        'Artificial intelligence has transformed many industries, including healthcare, finance, and transportation. Machine learning algorithms can now diagnose diseases, detect fraud, and optimize traffic flow.',
      wrongAnswer: 'AI algorithms can diagnose diseases.',
      whyWrong:
        'This is just one example; the main point is about AI transforming many industries',
    },
    howToAvoid:
      'Ask yourself if the answer captures the full scope of the passage or just one detail',
  },
  {
    id: 3,
    type: 'Too Broad',
    description:
      'Answer choices that go beyond what the passage actually discusses.',
    indicators: [
      'introduces new concepts',
      'makes claims not in passage',
      'overgeneralizes',
    ],
    example: {
      passage:
        'Studies of college students show that those who exercise regularly report better sleep quality.',
      wrongAnswer: 'Exercise improves health outcomes for all people.',
      whyWrong:
        'Passage only discusses college students and sleep quality, not all health outcomes for all people',
    },
    howToAvoid: 'Stay within the scope of what the passage actually covers',
  },
  {
    id: 4,
    type: 'Opposite Meaning',
    description:
      'Answer choices that contradict what the passage actually says.',
    indicators: [
      "reverses the author's position",
      'states the opposite',
      'negates the main point',
    ],
    example: {
      passage:
        'Research suggests that meditation can reduce stress and improve focus.',
      wrongAnswer: 'Meditation has been shown to increase stress levels.',
      whyWrong:
        'Directly contradicts the passage, which says meditation reduces stress',
    },
    howToAvoid:
      'Double-check that your answer aligns with, not contradicts, the passage',
  },
  {
    id: 5,
    type: 'Irrelevant',
    description:
      'Answer choices that discuss topics not addressed in the passage.',
    indicators: [
      'introduces unrelated topics',
      'off-topic content',
      'not mentioned in passage',
    ],
    example: {
      passage:
        'The development of renewable energy sources has accelerated due to declining costs and environmental concerns.',
      wrongAnswer:
        'Traditional fossil fuel companies are struggling financially.',
      whyWrong:
        'While possibly true, this is not discussed in the passage about renewable energy development',
    },
    howToAvoid:
      'Make sure the answer directly relates to content actually in the passage',
  },
  {
    id: 6,
    type: 'Not Supported',
    description:
      'Answer choices that make logical sense but lack evidence in the passage.',
    indicators: [
      'sounds reasonable',
      'makes logical sense',
      'but no textual evidence',
    ],
    example: {
      passage:
        'Urban gardens provide fresh vegetables for city residents and create green spaces in concrete environments.',
      wrongAnswer: 'Urban gardens reduce food costs for low-income families.',
      whyWrong:
        "While this might be true, the passage doesn't mention cost reduction or low-income families",
    },
    howToAvoid:
      'Find specific evidence in the passage to support your answer choice',
  },
  {
    id: 7,
    type: 'Assumption-Based',
    description:
      'Answer choices that require you to make assumptions not stated in the passage.',
    indicators: [
      'requires inferring unstated information',
      'assumes context not provided',
      'reads between lines incorrectly',
    ],
    example: {
      passage:
        'The new employee training program lasted six weeks and covered company policies.',
      wrongAnswer: 'The training program was expensive to implement.',
      whyWrong:
        'The passage mentions duration and content but says nothing about cost',
    },
    howToAvoid:
      'Base your answer only on information explicitly stated or clearly implied in the passage',
  },
];

const practiceQuestions = [
  {
    id: 1,
    passage: `Recent advances in solar panel technology have significantly improved efficiency rates, with some newer models achieving over 25% efficiency. This improvement, combined with falling manufacturing costs, has made solar energy increasingly competitive with traditional fossil fuels in many markets.

Government incentives and environmental regulations have also played a crucial role in promoting solar adoption. As a result, solar installations have grown rapidly over the past decade, though they still represent a small fraction of total energy generation globally.`,
    question:
      'Based on the passage, which of the following can be concluded about solar energy?',
    options: [
      {
        text: 'Solar energy will completely replace fossil fuels within the next decade.',
        trapType: 'Too Extreme',
        explanation:
          'Uses "completely replace" when passage only says solar is becoming "competitive"',
      },
      {
        text: 'Solar panel efficiency rates have improved to over 25%.',
        trapType: 'Too Narrow',
        explanation:
          'Focuses only on efficiency rates, missing the broader point about competitiveness',
      },
      {
        text: 'Renewable energy technologies are revolutionizing all aspects of modern society.',
        trapType: 'Too Broad',
        explanation:
          'Goes beyond solar energy to all renewables and all aspects of society',
      },
      {
        text: 'Recent technological and economic improvements have made solar energy more competitive with fossil fuels.',
        trapType: 'Correct',
        explanation:
          'Correctly captures the main point about efficiency + cost improvements leading to competitiveness',
      },
      {
        text: 'Solar energy remains too expensive to compete with fossil fuels.',
        trapType: 'Opposite',
        explanation:
          'Contradicts the passage, which says solar has become more competitive',
      },
    ],
    correct: 3,
  },
  {
    id: 2,
    passage: `The field of behavioral economics has challenged traditional economic assumptions by demonstrating that people often make irrational financial decisions. Research shows that individuals frequently exhibit loss aversion, preferring to avoid losses rather than acquire equivalent gains.

These insights have practical applications in policy design. For example, "nudging" techniques can guide people toward better choices without restricting their freedom to choose differently.`,
    question: 'What is the primary purpose of this passage?',
    options: [
      {
        text: 'To criticize traditional economics as completely wrong.',
        trapType: 'Too Extreme',
        explanation:
          'Says "completely wrong" when passage says "challenged," which is more moderate',
      },
      {
        text: 'To explain loss aversion in financial decision-making.',
        trapType: 'Too Narrow',
        explanation: 'Loss aversion is just one example; main point is broader',
      },
      {
        text: 'To describe how behavioral economics has challenged traditional assumptions and influenced policy.',
        trapType: 'Correct',
        explanation:
          'Captures both the challenge to traditional economics and practical applications',
      },
      {
        text: 'To argue that all government policies should use nudging techniques.',
        trapType: 'Too Broad',
        explanation:
          'Goes beyond what passage states about policy applications',
      },
      {
        text: 'To promote the superiority of traditional economic models.',
        trapType: 'Opposite',
        explanation:
          'Contradicts the passage, which discusses challenges to traditional models',
      },
    ],
    correct: 2,
  },
];

export default function TrapAnswersLesson() {
  const navigate = useNavigate();
  const practiceRef = useRef(null);
  const [selectedTrap, setSelectedTrap] = useState(null);
  const [practiceAnswers, setPracticeAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});

  const scrollToPractice = () => {
    practiceRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    setPracticeAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex,
    }));
  };

  const showExplanation = (questionId) => {
    setShowExplanations((prev) => ({
      ...prev,
      [questionId]: true,
    }));
  };

  const renderOverview = () => (
    <div className="lesson-content">
      <h2 className="section-title">Master the 7 Deadly Trap Answers</h2>

      <div className="lesson-intro">
        <p>
          The GRE deliberately includes trap answers designed to mislead you.
          Learning to recognize these 7 common trap types will dramatically
          improve your accuracy and confidence on Reading Comprehension
          questions.
        </p>
      </div>

      <div className="question-types-grid">
        {trapAnswers.map((trap) => (
          <div
            key={trap.id}
            onClick={() => setSelectedTrap(trap)}
            className={`question-type-card ${selectedTrap?.id === trap.id ? 'selected' : ''} trap-card`}
          >
            <div className="type-header">
              <div className="type-number warning">
                <FiAlertTriangle />
              </div>
              <h3 className="type-title">{trap.type}</h3>
            </div>
            <p className="type-description">{trap.description}</p>
          </div>
        ))}
      </div>

      {selectedTrap && (
        <div className="detailed-analysis">
          <h3 className="analysis-title">
            <FiAlertTriangle />
            {selectedTrap.type}
          </h3>

          <div className="analysis-section">
            <h4>How to Identify:</h4>
            <ul className="tips-list">
              {selectedTrap.indicators.map((indicator, index) => (
                <li key={index}>{indicator}</li>
              ))}
            </ul>
          </div>

          <div className="analysis-section">
            <h4>Example:</h4>
            <div
              style={{
                background: 'var(--liwaza-surface-container-highest)',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1rem',
              }}
            >
              <p style={{ fontStyle: 'italic', marginBottom: '0.5rem' }}>
                Passage excerpt: "{selectedTrap.example.passage}"
              </p>
              <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Question: {selectedTrap.example.question}
              </p>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
              >
                <div
                  style={{
                    background: 'var(--liwaza-error-container)',
                    color: 'var(--liwaza-on-error-container)',
                    padding: '0.75rem',
                    borderRadius: '6px',
                  }}
                >
                  <FiX style={{ marginRight: '0.5rem' }} />
                  <strong>Trap Answer:</strong>{' '}
                  {selectedTrap.example.wrongAnswer}
                </div>

                <div
                  style={{
                    background: 'var(--liwaza-success-container)',
                    color: 'var(--liwaza-on-success-container)',
                    padding: '0.75rem',
                    borderRadius: '6px',
                  }}
                >
                  <FiCheck style={{ marginRight: '0.5rem' }} />
                  <strong>Correct Answer:</strong>{' '}
                  {selectedTrap.example.correctAnswer}
                </div>
              </div>
            </div>

            <p
              style={{
                color: 'var(--liwaza-on-surface-variant)',
                fontStyle: 'italic',
              }}
            >
              <strong>Why it's a trap:</strong> {selectedTrap.example.whyWrong}
            </p>
          </div>

          <div className="analysis-section">
            <h4 className="tips-header">Avoidance Strategy:</h4>
            <div
              style={{
                background: 'var(--liwaza-primary-container)',
                color: 'var(--liwaza-on-primary-container)',
                padding: '1rem',
                borderRadius: '8px',
              }}
            >
              {selectedTrap.howToAvoid}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPractice = () => (
    <div ref={practiceRef} className="practice-section">
      <h2 className="section-title">Practice: Spot the Trap Answers</h2>

      {practiceQuestions.map((question) => (
        <div key={question.id} className="practice-passage">
          <h3 className="passage-title">Question {question.id}</h3>

          <div className="passage-text">
            <p>
              <strong>Passage excerpt:</strong>
            </p>
            <p style={{ fontStyle: 'italic' }}>{question.passage}</p>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <p
              style={{
                color: 'var(--liwaza-on-surface)',
                fontWeight: 'bold',
                marginBottom: '1rem',
              }}
            >
              {question.question}
            </p>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              {question.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(question.id, index)}
                  style={{
                    background:
                      practiceAnswers[question.id] === index
                        ? 'var(--liwaza-primary-container)'
                        : 'var(--liwaza-surface-container-highest)',
                    color:
                      practiceAnswers[question.id] === index
                        ? 'var(--liwaza-on-primary-container)'
                        : 'var(--liwaza-on-surface)',
                    border:
                      practiceAnswers[question.id] === index
                        ? '2px solid var(--liwaza-primary)'
                        : '1px solid var(--liwaza-outline-variant)',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  {String.fromCharCode(65 + index)}. {option.text}
                </button>
              ))}
            </div>

            {practiceAnswers[question.id] !== undefined && (
              <button
                onClick={() => showExplanation(question.id)}
                className="show-explanation-btn"
                style={{ marginTop: '1rem' }}
              >
                {showExplanations[question.id]
                  ? 'Hide Explanation'
                  : 'Show Explanation'}
              </button>
            )}

            {showExplanations[question.id] && (
              <div className="explanation-box">
                <div style={{ marginBottom: '1rem' }}>
                  <h5
                    style={{
                      color:
                        practiceAnswers[question.id] === question.correct
                          ? 'var(--liwaza-success)'
                          : 'var(--liwaza-error)',
                      marginBottom: '0.5rem',
                    }}
                  >
                    {practiceAnswers[question.id] === question.correct
                      ? '✓ Correct!'
                      : '✗ Incorrect'}
                  </h5>

                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Correct Answer:</strong>{' '}
                    {String.fromCharCode(65 + question.correct)}.{' '}
                    {question.options[question.correct].text}
                  </p>

                  <p style={{ marginBottom: '1rem' }}>
                    <strong>Explanation:</strong>{' '}
                    {question.options[question.correct].explanation}
                  </p>

                  {practiceAnswers[question.id] !== question.correct && (
                    <div
                      style={{
                        background: 'var(--liwaza-error-container)',
                        color: 'var(--liwaza-on-error-container)',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <strong>Your Answer Was:</strong>{' '}
                      {question.options[practiceAnswers[question.id]].trapType}
                      <br />
                      <span style={{ fontSize: '0.9rem' }}>
                        {
                          question.options[practiceAnswers[question.id]]
                            .explanation
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Strategy Summary */}
      <div className="lesson-intro">
        <h4
          style={{
            color: 'var(--liwaza-on-primary-container)',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiEye />
          Master Strategy Summary
        </h4>
        <ul
          style={{
            color: 'var(--liwaza-on-primary-container)',
            paddingLeft: '1rem',
            lineHeight: 1.6,
          }}
        >
          <li>Always return to the passage to verify your answer</li>
          <li>Be suspicious of answers that seem "too good to be true"</li>
          <li>Watch for extreme language (always, never, all, none)</li>
          <li>Eliminate answers that go beyond what the passage states</li>
          <li>
            Look for answers that address the specific scope of the question
          </li>
          <li>
            When in doubt, choose the most conservative answer that's directly
            supported
          </li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="lesson-container">
      {/* Header */}
      <div className="lesson-header">
        <button
          onClick={() =>
            navigate('/exams/gre/', { state: { activeCategory: 'reading' } })
          }
          className="back-button"
        >
          <FiArrowLeft /> Back to Reading Comprehension
        </button>

        <div className="header-actions">
          <button onClick={scrollToPractice} className="practice-button">
            Practice
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="lesson-title-section">
        <h1 className="lesson-title">
          <FiAlertTriangle />
          Lesson 7: 7 Trap Answers to Avoid
        </h1>
        <p className="lesson-subtitle">
          Learn to identify and avoid the most common trap answers that mislead
          test-takers on GRE Reading Comprehension questions. Master these
          patterns to improve your accuracy significantly.
        </p>
      </div>

      {/* Content */}
      {renderOverview()}
      {renderPractice()}

      {/* Get Started With Component */}
      <div style={{ margin: '3rem 0 2rem 0' }}>
        <GetStartedWith
          title="Need help avoiding Trap Answers?"
          spaceName="Verbal vault"
          humanCoachTitle="Talk to a GRE coach"
          liwazaAITitle="Ask AI"
          communityTitle="Ask other students"
          showHumanCoach={true}
          showLiwazaAI={true}
          showCommunity={true}
          aifirstmessage="I need help understanding how to identify and avoid Trap Answers in GRE Reading Comprehension"
          aicontext="The GRE deliberately includes trap answers designed to mislead you. Learning to recognize these 7 common trap types will dramatically improve your accuracy and confidence on Reading Comprehension questions."
        />
      </div>

      {/* Navigation */}
      <div className="lesson-navigation">
        <button
          onClick={() =>
            navigate('/exams/gre/reading-comprehension/lesson/speed-reading')
          }
          className="prev-lesson-button"
        >
          <FiArrowLeft /> Previous: Target 500 WPM
        </button>

        <div
          style={{
            background: 'var(--liwaza-success-container)',
            color: 'var(--liwaza-on-success-container)',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiCheck /> You've completed all lessons!
        </div>
      </div>
    </div>
  );
}
